// Types and wording for the Driving conduct page and the driver record's
// Driving tab (db/migrations/0369 to 0375).
//
// NOTHING HERE DECIDES ANYTHING. Whether an event counts, who it belongs to,
// whether a week is a breach, what the ladder gives and what to do next all
// arrive from the database (CLAUDE.md rule 4). This file only turns codes into
// words and moves a week start by seven days.
//
// A MISSING FIGURE SHOWS AS MISSING. A speed or a limit Cartrack did not send is
// '—', never 0 km/h, because "0" would read as a car standing still in a zone
// with no limit (CLAUDE.md rule 6).

export type EventKind = 'speeding' | 'reckless' | 'harsh_braking' | 'missed_service' | 'speeding_fine'
export type CaseCategory = 'driving_breach_week' | 'reckless_driving' | 'missed_designated_service'
export type CaseStatus = 'raised' | 'notice_issued' | 'response_received' | 'hearing_referred' | 'decided' | 'withdrawn'
export type Step = 'verbal_warning' | 'final_written_warning' | 'termination'

export type DrivingEvent = {
  event_id: string
  kind: EventKind
  driver_name?: string | null
  plate?: string | null
  event_date: string
  event_time: string
  place?: string | null
  speed_kmh?: number | null
  limit_kmh?: number | null
  over_by_kmh?: number | null
  duration_s?: number | string | null
  reason?: string | null
  attribution_status?: string | null
  excluded_reason?: string | null
  conduct_eligible?: boolean | null
  coaching_only_reason?: string | null
  counts?: boolean
  not_counted_because?: string | null
  excluded?: boolean
  instruction?: string | null
}

export type BreachRow = {
  driver_id: string
  driver_name: string | null
  manager_name: string | null
  window_start: string
  window_end: string
  speeding: number
  harsh_braking: number
  conduct_applies: boolean | null
  gate_date: string | null
  next_step: Step | null
  can_become_case: boolean | null
}

export type CaseRow = {
  case_id: string
  driver_id: string
  driver_name: string
  category: CaseCategory
  period_start: string
  period_end: string
  status: CaseStatus
  step: Step | null
  occurrences: number | null
  next_action: string | null
  manager_name: string | null
  can_act: boolean
}

export type ServiceRow = {
  booking_id: string
  ref: string | null
  plate: string | null
  scheduled_at: string
  place: string
  status: string
  is_designated: boolean
  missed: boolean
  instructed_driver_name: string | null
  instruction_channel: string | null
  instructed_at: string | null
  reason_label: string | null
  valid_reason_before: boolean | null
  no_case_reason: string | null
}

export type SettingRow = { key: string; value: number; effective_from: string; note: string; set_by: string }

export type DrivingConductPage = {
  week_start: string
  week_end: string
  today: string
  threshold: number
  summary: {
    events: number
    speeding: number
    reckless: number
    harsh_braking: number
    attributed: number
    unattributed: number
    counted: number
    conduct_eligible: number
    drivers_with_events: number
  }
  breaches: BreachRow[]
  reckless: DrivingEvent[]
  cases: CaseRow[]
  recently_decided: { case_id: string; driver_name: string; category: CaseCategory; period_start: string; outcome: string | null; decided_at: string | null }[]
  unattributed: { by_reason: Record<string, number>; events: DrivingEvent[] }
  services: ServiceRow[]
  settings: SettingRow[]
  gate: {
    agreements: { version: string; in_force_from: string | null; is_addendum: boolean; notes: string | null }[]
    drivers_signed: number
    drivers_active: number
  }
  weekly_report_send: boolean
  feed: {
    trips_done: number
    trips_waiting: number
    trips_failed: number
    last_read_at: string | null
    oldest_waiting: string | null
    limits?: {
      tiles_done: number
      tiles_total: number
      tiles_failed: number
      readings_matched: number
      readings_known: number
      readings_total: number
    }
  }
  options: {
    drivers: { driver_id: string; name: string; manager_name: string | null }[]
    reason_codes: { code: string; label: string; is_valid_excuse: boolean }[]
  }
  personLinked: boolean
}

export const KIND_LABEL: Record<EventKind, string> = {
  speeding: 'Speeding',
  reckless: 'Reckless',
  harsh_braking: 'Harsh braking',
  missed_service: 'Missed service',
  // A confirmed speeding fine in a case's evidence (clause 21.6A, 0482).
  speeding_fine: 'Speeding fine',
}

export const CATEGORY_LABEL: Record<CaseCategory, string> = {
  driving_breach_week: 'Breach week',
  reckless_driving: 'Reckless driving',
  missed_designated_service: 'Missed designated service',
}

export const STEP_LABEL: Record<Step, string> = {
  verbal_warning: 'Verbal warning',
  final_written_warning: 'Final written warning',
  termination: 'Dismissal, after a hearing',
}

export const STATUS_LABEL: Record<CaseStatus, string> = {
  raised: 'Raised, notice not yet recorded',
  notice_issued: 'Notice given',
  response_received: 'Response recorded',
  hearing_referred: 'Referred to a hearing',
  decided: 'Decided',
  withdrawn: 'Withdrawn',
}

export const UNATTRIBUTED_LABEL: Record<string, string> = {
  no_shift: 'No shift on that car',
  two_drivers: 'Two drivers on shift',
  handover_window: 'In the handover window',
  allocation_disagrees: 'Shift and allocation disagree',
  no_vehicle: 'Unit not linked to a car',
}

export const NOTICE_METHODS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'letter', label: 'Letter' },
  { value: 'in_person_signed', label: 'In person, signed' },
] as const

export const EXCLUSIONS = [
  { value: 'dashcam', label: 'Dashcam footage' },
  { value: 'telematics', label: 'Telematics' },
  { value: 'credible_account', label: 'Another credible account' },
  { value: 'data_fault', label: 'The data is wrong' },
] as const

export const INSTRUCTION_CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'letter', label: 'Letter' },
] as const

/** '—' for a figure Cartrack did not send. Never 0. */
export function kmh(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${value} km/h`
}

/**
 * "112 km/h in a 80" or "137 km/h, no known limit". The limit is the verified
 * one (db/migrations/0378): where no road sign is known, only 130 and 140 apply.
 */
export function speedLine(e: Pick<DrivingEvent, 'speed_kmh' | 'limit_kmh'>): string {
  if (e.speed_kmh === null || e.speed_kmh === undefined) return '—'
  return e.limit_kmh === null || e.limit_kmh === undefined
    ? `${e.speed_kmh} km/h, no known limit`
    : `${e.speed_kmh} km/h in a ${e.limit_kmh}`
}

/** Whole seconds, or '—' for a harsh braking event, which has no length. */
export function seconds(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  return Number.isFinite(n) ? `${Math.round(n)} s` : '—'
}

/** A week start moved by whole weeks. Dates only, parsed as UTC, so it cannot slip a day. */
export function shiftWeek(weekStart: string, weeks: number): string {
  const d = new Date(`${weekStart}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + weeks * 7)
  return d.toISOString().slice(0, 10)
}

/** '2026-09-28' -> 'Mon 28 Sep'. */
export function dayLabel(date: string): string {
  const d = new Date(`${String(date).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return String(date)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${days[d.getUTCDay()]} ${d.getUTCDate()} ${months[d.getUTCMonth()]}`
}

/** A timestamp as SAST wall time, 'Mon 28 Sep 14:05'. */
export function sastStamp(ts: string | null | undefined): string {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return String(ts)
  const sast = new Date(d.getTime() + 2 * 3600 * 1000)
  const hh = String(sast.getUTCHours()).padStart(2, '0')
  const mm = String(sast.getUTCMinutes()).padStart(2, '0')
  return `${dayLabel(sast.toISOString().slice(0, 10))} ${hh}:${mm}`
}

export function stepTone(step: Step | null | undefined): 'amber' | 'red' | 'neutral' {
  if (step === 'termination') return 'red'
  if (step === 'final_written_warning' || step === 'verbal_warning') return 'amber'
  return 'neutral'
}

/** Which settings rows are thresholds from the agreement and which are ours. */
export function isAgreementSetting(s: Pick<SettingRow, 'note'>): boolean {
  return /^Clause /.test(s.note)
}

/**
 * Whether an event counts, in words. The reason codes come from v_driving_events
 * (db/migrations/0370); this only reads them out.
 */
export function standingLabel(
  e: Pick<DrivingEvent, 'excluded_reason' | 'not_counted_because' | 'conduct_eligible' | 'coaching_only_reason'>,
): string {
  const why = e.excluded_reason ?? e.not_counted_because ?? null
  if (why) {
    if (why.startsWith('unattributed: ')) {
      const code = why.slice('unattributed: '.length)
      return `Not attributed: ${(UNATTRIBUTED_LABEL[code] ?? code).toLowerCase()}`
    }
    if (why === 'counted_as_reckless') return 'Not counted: inside a reckless period'
    if (why.startsWith('upheld: ')) return "Not counted: the driver's claim was upheld"
    return why
  }
  if (e.conduct_eligible) return 'Counts, and can become a case'
  const c = e.coaching_only_reason
  return c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Counts'
}

/**
 * How far the road matching has got, in one sentence. Until a reading is matched
 * its speed is judged on 130 and 140 only, so a page that hid this would make a
 * quiet week look clean.
 */
export function limitsLine(l: DrivingConductPage['feed']['limits'] | undefined): string | null {
  if (!l) return null
  const pct = (n: number, d: number) => (d > 0 ? Math.round((100 * n) / d) : 0)
  if (l.readings_matched >= l.readings_total && l.readings_total > 0) {
    return `Speed limits are checked against road signs for every reading; ${pct(l.readings_known, l.readings_total)}% are on a road with a known limit.`
  }
  return `Speed limits checked against road signs for ${l.readings_matched.toLocaleString('en-ZA')} of ${l.readings_total.toLocaleString('en-ZA')} readings so far (${pct(l.readings_matched, l.readings_total)}%). Until a reading is checked, only the 130 and 140 km/h lines apply to it.`
}
